import * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import { useDialogRef, dialogPanelProps } from '../../components/a11y';
import { Switch } from '@/src/shadComponents/ui/switch';
import useFeatureFlagManagement from '@/src/Features/components/hooks/useFeatureFlagManagement';

/*
 * Feature flags admin — the flags and their role associations. Flag
 * switches apply immediately (optimistic, reverting on failure); the
 * role matrix batches changes behind a Save, replacing classic's
 * fire-on-every-checkbox editor (the resolved ADMIN-MERGE decision).
 */

interface FlagRow {
    id: number;
    key: string;
    enabled: boolean;
    enabledForDevelopment: boolean;
    isPublic?: boolean;
}

interface RoleRow {
    name: string;
    roleId: number;
    keys: string[];
}

const FIELD_LABEL =
    'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

export default function AdminFeaturesV2() {
    const {
        fetchFeatureFlags,
        createFeatureFlag,
        updateFeatureFlag,
        deleteFeatureFlag,
        getFeatureFlagsForRoles,
        addFeatureFlagToRole,
        removeFeatureFlagFromRole,
    } = useFeatureFlagManagement();

    const [flags, setFlags] = useState<FlagRow[]>([]);
    const [roles, setRoles] = useState<RoleRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [draft, setDraft] = useState({
        key: '',
        enabled: false,
        enabledForDevelopment: true,
        isPublic: false,
    });
    const [roleDialog, setRoleDialog] = useState<RoleRow | null>(null);
    const [roleDraft, setRoleDraft] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);

    const anyDialog = createOpen || roleDialog != null;
    const closeDialogs = () => {
        setCreateOpen(false);
        setRoleDialog(null);
    };
    const dialogRef = useDialogRef(anyDialog, closeDialogs);

    const loadRoles = async () => {
        const lookup = (await getFeatureFlagsForRoles().catch(
            () => ({}),
        )) as Record<string, { keys: string[]; roleId: number }>;
        setRoles(
            Object.entries(lookup).map(([name, entry]) => ({
                name,
                roleId: entry.roleId,
                keys: entry.keys,
            })),
        );
    };

    useEffect(() => {
        let mounted = true;
        Promise.all([
            fetchFeatureFlags().catch(() => []),
            loadRoles(),
        ]).then(([flagRows]) => {
            if (!mounted) return;
            setFlags(
                [...(flagRows ?? [])].sort((a: FlagRow, b: FlagRow) =>
                    a.key.localeCompare(b.key),
                ),
            );
            setIsLoading(false);
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* optimistic switch — immediate apply, revert on failure */
    const toggle = async (
        row: FlagRow,
        field: 'enabled' | 'enabledForDevelopment' | 'isPublic',
        value: boolean,
    ) => {
        const prev = flags;
        const next = { ...row, [field]: value };
        setFlags((current) =>
            current.map((f) => (f.id === row.id ? next : f)),
        );
        const ok = await updateFeatureFlag(next)
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            setFlags(prev);
            toast.error("Couldn't update the flag.");
        }
    };

    const submitCreate = async () => {
        if (busy || !draft.key.trim()) return;
        setBusy(true);
        setDialogError(null);
        const ok = await createFeatureFlag({
            ...draft,
            key: draft.key.trim().toUpperCase(),
        }).catch(() => null);
        setBusy(false);
        if (ok) {
            setCreateOpen(false);
            toast.success('Flag created');
            setFlags(
                [...((await fetchFeatureFlags().catch(() => [])) ?? [])].sort(
                    (a: FlagRow, b: FlagRow) => a.key.localeCompare(b.key),
                ),
            );
        } else {
            setDialogError("Couldn't create the flag.");
        }
    };

    const removeFlag = async (row: FlagRow) => {
        if (
            !window.confirm(
                `Delete ${row.key}? Anything gated on it turns off everywhere.`,
            )
        )
            return;
        const prev = flags;
        setFlags((current) => current.filter((f) => f.id !== row.id));
        const ok = await deleteFeatureFlag(row.key)
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            setFlags(prev);
            toast.error("Couldn't delete the flag.");
        }
    };

    /* role matrix — batched save */
    const openRole = (role: RoleRow) => {
        setRoleDraft(new Set(role.keys));
        setDialogError(null);
        setRoleDialog(role);
    };

    const submitRole = async () => {
        if (!roleDialog || busy) return;
        const before = new Set(roleDialog.keys);
        const additions = flags.filter(
            (f) => roleDraft.has(f.key) && !before.has(f.key),
        );
        const removals = flags.filter(
            (f) => !roleDraft.has(f.key) && before.has(f.key),
        );
        if (additions.length === 0 && removals.length === 0) {
            closeDialogs();
            return;
        }
        setBusy(true);
        setDialogError(null);
        const results: boolean[] = [];
        for (const flag of additions) {
            results.push(
                await addFeatureFlagToRole(roleDialog.roleId, flag.id)
                    .then(() => true)
                    .catch(() => false),
            );
        }
        for (const flag of removals) {
            results.push(
                await removeFeatureFlagFromRole(roleDialog.roleId, flag.id)
                    .then(() => true)
                    .catch(() => false),
            );
        }
        const failed = results.filter((ok) => !ok).length;
        setBusy(false);
        await loadRoles();
        if (failed === 0) {
            closeDialogs();
            toast.success('Role access saved');
        } else {
            setDialogError(
                `${failed} change${failed === 1 ? '' : 's'} failed — the list shows what actually saved.`,
            );
        }
    };

    if (isLoading) {
        return (
            <div className="mb-6 h-40 animate-pulse rounded-[16px] bg-secondary" />
        );
    }

    return (
        <>
            <SettingsCard
                title="Feature flags"
                desc="Switches apply immediately, everywhere."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            {flags.length} flag
                            {flags.length === 1 ? '' : 's'}
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                setDraft({
                                    key: '',
                                    enabled: false,
                                    enabledForDevelopment: true,
                                    isPublic: false,
                                });
                                setDialogError(null);
                                setCreateOpen(true);
                            }}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            Add flag
                        </button>
                    </>
                }
            >
                <div className="mb-1 flex items-center gap-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-faint">
                    <span className="min-w-0 flex-1">Key</span>
                    <span className="w-[52px] text-center">Live</span>
                    <span className="w-[52px] text-center">Dev</span>
                    <span className="w-[52px] text-center">Public</span>
                    <span className="w-[20px]" />
                </div>
                {flags.map((row) => (
                    <div
                        key={row.id}
                        className="group flex items-center gap-3 border-t border-line py-[8px] text-[12.5px]"
                    >
                        <span className="min-w-0 flex-1 truncate font-mono text-[11px] font-semibold">
                            {row.key}
                        </span>
                        <span className="flex w-[52px] justify-center">
                            <Switch
                                checked={row.enabled}
                                onCheckedChange={(value) =>
                                    toggle(row, 'enabled', value)
                                }
                            />
                        </span>
                        <span className="flex w-[52px] justify-center">
                            <Switch
                                checked={row.enabledForDevelopment}
                                onCheckedChange={(value) =>
                                    toggle(
                                        row,
                                        'enabledForDevelopment',
                                        value,
                                    )
                                }
                            />
                        </span>
                        <span className="flex w-[52px] justify-center">
                            <Switch
                                checked={!!row.isPublic}
                                onCheckedChange={(value) =>
                                    toggle(row, 'isPublic', value)
                                }
                            />
                        </span>
                        <button
                            type="button"
                            title="Delete flag"
                            onClick={() => removeFlag(row)}
                            className="w-[20px] cursor-pointer border-0 bg-transparent p-0 text-center font-[inherit] text-[13px] text-faint opacity-0 transition-opacity hover:text-attention focus-visible:opacity-100 group-hover:opacity-100"
                        >
                            ×
                        </button>
                    </div>
                ))}
            </SettingsCard>

            <SettingsCard
                title="Role access"
                desc="Which flags each account role sees. Changes batch behind Save."
            >
                {roles.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        No role associations yet.
                    </p>
                )}
                {roles.map((role) => (
                    <div
                        key={role.roleId}
                        className="group flex items-center gap-3 border-b border-line py-[9px] text-[12.5px] last:border-b-0"
                    >
                        <span className="w-[130px] flex-none font-semibold">
                            {role.name}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {role.keys.length} flag
                            {role.keys.length === 1 ? '' : 's'}
                        </span>
                        <button
                            type="button"
                            onClick={() => openRole(role)}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
                        >
                            edit
                        </button>
                    </div>
                ))}
            </SettingsCard>

            {createOpen && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={closeDialogs}
                >
                    <div
                        ref={dialogRef}
                        {...dialogPanelProps('Add feature flag')}
                        className="w-full max-w-[380px] rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-3.5">
                            Add feature flag
                        </div>
                        <label className={FIELD_LABEL}>Key</label>
                        <input
                            autoFocus
                            className="ps-input mb-3 w-full font-mono text-[12px] uppercase"
                            placeholder="MY_FEATURE_V1"
                            value={draft.key}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    key: e.target.value,
                                }))
                            }
                        />
                        {(
                            [
                                ['enabled', 'Enabled (live)'],
                                [
                                    'enabledForDevelopment',
                                    'Enabled for development',
                                ],
                                ['isPublic', 'Public (unauthenticated)'],
                            ] as const
                        ).map(([field, label]) => (
                            <label
                                key={field}
                                className="mb-2 flex cursor-pointer items-center justify-between gap-2 text-[12.5px] text-muted-foreground"
                            >
                                {label}
                                <Switch
                                    checked={draft[field]}
                                    onCheckedChange={(value) =>
                                        setDraft((d) => ({
                                            ...d,
                                            [field]: value,
                                        }))
                                    }
                                />
                            </label>
                        ))}
                        <div className="mt-4 flex items-center gap-3">
                            <button
                                type="button"
                                disabled={busy || !draft.key.trim()}
                                onClick={submitCreate}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {busy ? 'Creating…' : 'Create flag'}
                            </button>
                            <button
                                type="button"
                                onClick={closeDialogs}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                        {dialogError && (
                            <p className="mt-2.5 text-[12px] font-semibold text-attention">
                                {dialogError}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {roleDialog && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={closeDialogs}
                >
                    <div
                        ref={dialogRef}
                        {...dialogPanelProps(
                            `Role access — ${roleDialog.name}`,
                        )}
                        className="w-full max-w-[420px] rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-3.5">
                            Role access — {roleDialog.name}
                        </div>
                        <div className="max-h-[320px] overflow-y-auto">
                            {flags.map((flag) => (
                                <label
                                    key={flag.id}
                                    className="flex cursor-pointer items-center gap-2.5 rounded-[8px] px-1.5 py-[7px] text-[12.5px] hover:bg-secondary"
                                >
                                    <input
                                        type="checkbox"
                                        checked={roleDraft.has(flag.key)}
                                        onChange={() =>
                                            setRoleDraft((current) => {
                                                const next = new Set(
                                                    current,
                                                );
                                                if (next.has(flag.key))
                                                    next.delete(flag.key);
                                                else next.add(flag.key);
                                                return next;
                                            })
                                        }
                                    />
                                    <span className="font-mono text-[11px]">
                                        {flag.key}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div className="mt-4 flex items-center gap-3">
                            <button
                                type="button"
                                disabled={busy}
                                onClick={submitRole}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {busy ? 'Saving…' : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={closeDialogs}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                        {dialogError && (
                            <p className="mt-2.5 text-[12px] font-semibold text-attention">
                                {dialogError}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
